import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  inject,
  input,
  viewChild,
} from "@angular/core";

export type TooltipPosition = "top" | "bottom" | "left" | "right";

// same gaps as the hover offsets in tooltip.component.scss
const TOOLTIP_GAP_VERTICAL = 10;
const TOOLTIP_GAP_HORIZONTAL = 15;

// Viewport coordinates for a detached tooltip, kept inside the viewport.
export function computeTooltipPosition(
  position: TooltipPosition,
  host: { left: number; right: number; top: number; bottom: number; width: number; height: number },
  tooltip: { width: number; height: number },
  viewport: { width: number; height: number },
  margin = 8
): { top: number; left: number } {
  let top: number;
  let left: number;
  switch (position) {
    case "bottom":
      top = host.bottom + TOOLTIP_GAP_VERTICAL;
      left = host.left + host.width / 2 - tooltip.width / 2;
      break;
    case "left":
      top = host.top + host.height / 2 - tooltip.height / 2;
      left = host.left - tooltip.width - TOOLTIP_GAP_HORIZONTAL;
      break;
    case "right":
      top = host.top + host.height / 2 - tooltip.height / 2;
      left = host.right + TOOLTIP_GAP_HORIZONTAL;
      break;
    case "top":
    default:
      top = host.top - tooltip.height - TOOLTIP_GAP_VERTICAL;
      left = host.left + host.width / 2 - tooltip.width / 2;
      break;
  }
  const clamp = (value: number, size: number, max: number) =>
    Math.max(margin, Math.min(value, max - margin - size));
  return {
    top: clamp(top, tooltip.height, viewport.height),
    left: clamp(left, tooltip.width, viewport.width),
  };
}

@Component({
  selector: "av-tooltip",
  templateUrl: "./tooltip.component.html",
  styleUrls: ["./tooltip.component.scss"],
  standalone: false,
})
export class TooltipComponent implements OnInit {
  private host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly help = input<boolean>(undefined);
  readonly message = input<string>(undefined);
  readonly enabled = input(true);
  readonly position = input<TooltipPosition>("top");
  readonly wrap = input<boolean>(undefined);
  /**
   * Horizontal anchoring for top/bottom tooltips. "center" (default) centres
   * the bubble on the trigger; "start"/"end" pin its left/right edge to the
   * trigger instead, for triggers that sit at a container edge where a centred
   * bubble would be clipped.
   */
  readonly align = input<"center" | "start" | "end">("center");
  // render the tooltip in the top layer (fixed to the viewport) so that it is
  // not clipped by a scrolling / overflow:hidden parent
  readonly detached = input(false);

  readonly tooltipRef = viewChild<ElementRef<HTMLElement>>("tooltip");

  constructor() {}

  ngOnInit(): void {}

  @HostListener("mouseenter")
  show() {
    const el = this.tooltipRef()?.nativeElement;
    if (!this.detached() || !el) return;
    // not supported in older browsers, position: fixed alone is the fallback
    if (typeof el.showPopover === "function" && !el.matches(":popover-open")) {
      el.showPopover();
    }
    // the tooltip may live in the picture-in-picture window
    const view = this.host.nativeElement.ownerDocument.defaultView ?? window;
    const { top, left } = computeTooltipPosition(
      this.position(),
      this.host.nativeElement.getBoundingClientRect(),
      el.getBoundingClientRect(),
      { width: view.innerWidth, height: view.innerHeight }
    );
    el.style.top = `${top}px`;
    el.style.left = `${left}px`;
  }

  @HostListener("mouseleave")
  hide() {
    const el = this.tooltipRef()?.nativeElement;
    if (!this.detached() || !el) return;
    if (typeof el.hidePopover === "function" && el.matches(":popover-open")) {
      el.hidePopover();
    }
  }
}
